import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

const B = {
  gold: "#D4A843", green: "#43B581", red: "#E74C3C", orange: "#FF6B35",
  bg: "#0D0F13", card: "#13161C", border: "#1E222A",
  white: "#F0F0F0", whiteGray: "#A0A8B8", gray: "#6B7280",
};

function Stat({ label, value, sub, color = B.white, testid }) {
  return (
    <div data-testid={testid} style={{
      background: B.card, border: `1px solid ${B.border}`, borderRadius: 12,
      padding: "16px 20px", flex: "1 1 180px", minWidth: 180,
    }}>
      <div style={{ color: B.gray, fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>{label}</div>
      <div style={{ color, fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ color: B.whiteGray, fontSize: 11, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function Bar({ value, max, label }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: B.whiteGray, fontFamily: "monospace" }}>
      <span style={{ width: 64, color: B.gray }}>{label}</span>
      <div style={{ flex: 1, height: 22, background: B.border, borderRadius: 4, overflow: "hidden", position: "relative" }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: `linear-gradient(90deg, ${B.gold}, ${B.gold}cc)`,
        }} />
        <span style={{
          position: "absolute", right: 8, top: 3, color: B.white, fontSize: 11, fontWeight: 700,
        }}>{value}</span>
      </div>
    </div>
  );
}

function BudgetBar({ label, current, cap }) {
  const pctRaw = cap > 0 ? (current / cap) * 100 : 0;
  const pct = Math.max(2, Math.min(100, pctRaw));
  const color = pctRaw >= 100 ? B.red : pctRaw >= 80 ? B.orange : B.green;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, marginBottom: 6 }}>
      <span style={{ width: 64, color: B.gray, fontFamily: "monospace" }}>{label}</span>
      <div style={{ flex: 1, height: 18, background: B.border, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 0.4s, background 0.3s" }} />
      </div>
      <span style={{ minWidth: 110, textAlign: "right", color, fontFamily: "monospace", fontWeight: 700 }}>
        ${current.toFixed(3)} / ${cap.toFixed(2)}
      </span>
    </div>
  );
}

export default function AxelAdminPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [confirmPause, setConfirmPause] = useState(false);
  const [pauseReason, setPauseReason] = useState("AXEL is briefly paused while we top up credits — back shortly 🔧");
  // Budget guardrail form state
  const [budgetEnabled, setBudgetEnabled] = useState(false);
  const [budgetDaily, setBudgetDaily] = useState("");
  const [budgetWeekly, setBudgetWeekly] = useState("");
  const [budgetEmail, setBudgetEmail] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/axel/stats`, ax);
      setStats(data);
      // Sync form state from server (only on first load — avoid overwriting user edits on auto-refresh)
      setBudgetEnabled((prev) => prev || data.budget_daily_usd != null || data.budget_weekly_usd != null);
      setBudgetDaily((prev) => prev || (data.budget_daily_usd != null ? String(data.budget_daily_usd) : ""));
      setBudgetWeekly((prev) => prev || (data.budget_weekly_usd != null ? String(data.budget_weekly_usd) : ""));
      setBudgetEmail((prev) => prev || data.budget_alert_email || "");
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
    const id = setInterval(fetchStats, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, [fetchStats]);

  const showToast = (msg, color = B.green) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2800);
  };

  const togglePause = async (paused) => {
    try {
      await axios.post(`${API}/admin/axel/toggle`, {
        paused,
        reason: paused ? pauseReason : null,
      }, ax);
      showToast(paused ? "AXEL paused for all students" : "AXEL resumed", paused ? B.orange : B.green);
      setConfirmPause(false);
      fetchStats();
    } catch (e) {
      showToast("Failed to update — try again", B.red);
    }
  };

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const payload = {
        daily_usd: budgetEnabled && budgetDaily ? parseFloat(budgetDaily) : null,
        weekly_usd: budgetEnabled && budgetWeekly ? parseFloat(budgetWeekly) : null,
        alert_email: budgetEmail || null,
      };
      await axios.post(`${API}/admin/axel/budget`, payload, ax);
      showToast("Budget guardrail saved ✓", B.green);
      fetchStats();
    } catch (e) {
      showToast("Failed to save budget", B.red);
    }
    setSavingBudget(false);
  };

  if (loading) {
    return <div style={{ padding: 24, color: B.whiteGray }}>Loading AXEL usage…</div>;
  }
  if (!stats) {
    return <div style={{ padding: 24, color: B.red }}>Failed to load AXEL stats.</div>;
  }

  const maxDay = Math.max(...stats.by_day.map((d) => d.messages), 1);

  return (
    <div data-testid="axel-admin-panel" style={{ padding: "16px 24px 40px" }}>
      {toast && (
        <div data-testid="axel-admin-toast" style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 10000,
          background: B.card, border: `1px solid ${toast.color}40`, borderRadius: 10,
          padding: "12px 24px", color: toast.color, fontSize: 13, fontWeight: 600,
        }}>{toast.msg}</div>
      )}

      {/* Pause Banner */}
      <div data-testid="axel-pause-banner" style={{
        background: stats.paused ? `${B.orange}15` : `${B.green}10`,
        border: `1px solid ${stats.paused ? B.orange : B.green}40`,
        borderLeft: `4px solid ${stats.paused ? B.orange : B.green}`,
        borderRadius: 10, padding: "14px 18px", marginBottom: 18,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ color: stats.paused ? B.orange : B.green, fontSize: 12, fontWeight: 800, letterSpacing: 1.2, display: "flex", alignItems: "center", gap: 8 }}>
            {stats.paused ? "⏸ AXEL IS PAUSED" : "● AXEL IS LIVE"}
            {stats.auto_paused && (
              <span data-testid="axel-auto-paused-badge" style={{
                background: `${B.orange}30`, color: B.orange, padding: "2px 8px", borderRadius: 999,
                fontSize: 9, letterSpacing: 1.5, fontWeight: 800,
              }}>AUTO-PAUSED · BUDGET CAP</span>
            )}
          </div>
          <div style={{ color: B.whiteGray, fontSize: 12, marginTop: 4 }}>
            {stats.paused
              ? `Paused${stats.paused_by ? ` by ${stats.paused_by}` : ""}${stats.paused_at ? ` · ${new Date(stats.paused_at).toLocaleString()}` : ""}`
              : `Model: ${stats.model} · Limit: ${stats.daily_limit}/student/day`}
          </div>
          {stats.paused && stats.paused_reason && (
            <div style={{ color: B.white, fontSize: 12, marginTop: 6, fontStyle: "italic" }}>"{stats.paused_reason}"</div>
          )}
        </div>
        {stats.paused ? (
          <button
            data-testid="axel-resume-btn"
            onClick={() => togglePause(false)}
            style={{
              background: B.green, color: "#0D0F13", border: "none", borderRadius: 8,
              padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}
          >
            ▶ Resume AXEL
          </button>
        ) : (
          <button
            data-testid="axel-pause-btn"
            onClick={() => setConfirmPause(true)}
            style={{
              background: "transparent", color: B.orange, border: `1px solid ${B.orange}`,
              borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}
          >
            ⏸ Pause AXEL
          </button>
        )}
      </div>

      {/* Confirm pause modal */}
      {confirmPause && (
        <div data-testid="axel-pause-modal" style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 10001,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div style={{
            background: B.card, border: `1px solid ${B.orange}50`, borderRadius: 14,
            padding: 22, maxWidth: 460, width: "100%",
          }}>
            <div style={{ color: B.orange, fontWeight: 800, fontSize: 14, letterSpacing: 1, marginBottom: 8 }}>
              ⏸ PAUSE AXEL FOR ALL STUDENTS
            </div>
            <div style={{ color: B.whiteGray, fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>
              Students will see your reason in the chat window. Admins/VIPs are not affected.
            </div>
            <textarea
              data-testid="axel-pause-reason-input"
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              rows={3}
              style={{
                width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${B.border}`,
                background: B.bg, color: B.white, fontSize: 13, resize: "none", outline: "none",
                fontFamily: "inherit",
              }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
              <button onClick={() => setConfirmPause(false)} style={{
                background: "transparent", color: B.whiteGray, border: `1px solid ${B.border}`,
                borderRadius: 8, padding: "8px 16px", fontSize: 12, cursor: "pointer",
              }}>Cancel</button>
              <button
                data-testid="axel-confirm-pause-btn"
                onClick={() => togglePause(true)}
                style={{
                  background: B.orange, color: "#0D0F13", border: "none",
                  borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}
              >Pause AXEL</button>
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
        <Stat testid="stat-msgs-today" label="Messages Today" value={stats.messages_today} sub={`${stats.active_users_today} active student${stats.active_users_today === 1 ? "" : "s"}`} color={B.gold} />
        <Stat testid="stat-cost-today" label="Cost Today" value={`$${stats.estimated_cost_today_usd.toFixed(4)}`} sub={`avg $${stats.avg_cost_per_msg_usd.toFixed(5)}/msg · actual`} color={B.green} />
        <Stat testid="stat-msgs-week" label="Messages (7d)" value={stats.messages_week} sub={stats.tokens_week ? `${((stats.tokens_week.prompt + stats.tokens_week.completion) / 1000).toFixed(1)}k tokens` : ""} color={B.white} />
        <Stat testid="stat-cost-week" label="Cost (7d)" value={`$${stats.estimated_cost_week_usd.toFixed(4)}`} sub={`in: $${stats.input_price_per_m}/M · out: $${stats.output_price_per_m}/M`} color={B.green} />
      </div>

      {/* Budget Guardrail */}
      <div data-testid="axel-budget-card" style={{
        background: B.card, border: `1px solid ${B.border}`, borderRadius: 12,
        padding: 18, marginBottom: 18,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ color: B.white, fontSize: 13, fontWeight: 700, letterSpacing: 0.5 }}>
              Cost Guardrail — auto-pause when budget is reached
            </div>
            <div style={{ color: B.gray, fontSize: 11, marginTop: 4 }}>
              AXEL will pause itself + email you the moment estimated spend crosses the cap. Zero-surprise protection.
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
            <input
              data-testid="axel-budget-toggle"
              type="checkbox"
              checked={budgetEnabled}
              onChange={(e) => setBudgetEnabled(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: B.gold }}
            />
            <span style={{ color: budgetEnabled ? B.gold : B.whiteGray, fontSize: 12, fontWeight: 600 }}>
              {budgetEnabled ? "ENABLED" : "DISABLED"}
            </span>
          </label>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", opacity: budgetEnabled ? 1 : 0.5, transition: "opacity 0.2s" }}>
          <div style={{ flex: "1 1 160px", minWidth: 140 }}>
            <div style={{ color: B.gray, fontSize: 10, letterSpacing: 1.2, marginBottom: 4 }}>DAILY CAP (USD)</div>
            <input
              data-testid="axel-budget-daily-input"
              type="number"
              step="0.10"
              min="0"
              placeholder="e.g. 1.00"
              disabled={!budgetEnabled}
              value={budgetDaily}
              onChange={(e) => setBudgetDaily(e.target.value)}
              style={{
                width: "100%", padding: "8px 12px", borderRadius: 6,
                border: `1px solid ${B.border}`, background: B.bg, color: B.white,
                fontSize: 14, outline: "none", fontFamily: "monospace",
              }}
            />
          </div>
          <div style={{ flex: "1 1 160px", minWidth: 140 }}>
            <div style={{ color: B.gray, fontSize: 10, letterSpacing: 1.2, marginBottom: 4 }}>WEEKLY CAP (USD)</div>
            <input
              data-testid="axel-budget-weekly-input"
              type="number"
              step="0.50"
              min="0"
              placeholder="e.g. 5.00"
              disabled={!budgetEnabled}
              value={budgetWeekly}
              onChange={(e) => setBudgetWeekly(e.target.value)}
              style={{
                width: "100%", padding: "8px 12px", borderRadius: 6,
                border: `1px solid ${B.border}`, background: B.bg, color: B.white,
                fontSize: 14, outline: "none", fontFamily: "monospace",
              }}
            />
          </div>
          <div style={{ flex: "2 1 220px", minWidth: 180 }}>
            <div style={{ color: B.gray, fontSize: 10, letterSpacing: 1.2, marginBottom: 4 }}>ALERT EMAIL</div>
            <input
              data-testid="axel-budget-email-input"
              type="email"
              placeholder="admin@flexxacademy.com"
              disabled={!budgetEnabled}
              value={budgetEmail}
              onChange={(e) => setBudgetEmail(e.target.value)}
              style={{
                width: "100%", padding: "8px 12px", borderRadius: 6,
                border: `1px solid ${B.border}`, background: B.bg, color: B.white,
                fontSize: 14, outline: "none",
              }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            data-testid="axel-budget-save-btn"
            onClick={saveBudget}
            disabled={savingBudget}
            style={{
              background: B.gold, color: "#0D0F13", border: "none", borderRadius: 8,
              padding: "8px 18px", fontSize: 12, fontWeight: 700, cursor: savingBudget ? "wait" : "pointer",
              opacity: savingBudget ? 0.6 : 1,
            }}
          >
            {savingBudget ? "Saving..." : "Save Guardrail"}
          </button>
        </div>
        {/* Live progress vs caps */}
        {(stats.budget_daily_usd != null || stats.budget_weekly_usd != null) && (
          <div data-testid="axel-budget-progress" style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${B.border}` }}>
            {stats.budget_daily_usd != null && (
              <BudgetBar label="Today" current={stats.estimated_cost_today_usd} cap={stats.budget_daily_usd} />
            )}
            {stats.budget_weekly_usd != null && (
              <BudgetBar label="7-Day" current={stats.estimated_cost_week_usd} cap={stats.budget_weekly_usd} />
            )}
          </div>
        )}
      </div>

      {/* 7-day chart */}
      <div style={{
        background: B.card, border: `1px solid ${B.border}`, borderRadius: 12,
        padding: 18, marginBottom: 18,
      }}>
        <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 14, letterSpacing: 0.5 }}>
          Messages — Last 7 Days
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {stats.by_day.map((d) => (
            <Bar key={d.day} label={d.day.slice(5)} value={d.messages} max={maxDay} />
          ))}
        </div>
      </div>

      {/* Top users */}
      <div style={{
        background: B.card, border: `1px solid ${B.border}`, borderRadius: 12,
        padding: 18,
      }}>
        <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 14, letterSpacing: 0.5 }}>
          Top Users Today
        </div>
        {stats.top_users_today.length === 0 ? (
          <div style={{ color: B.gray, fontSize: 12, textAlign: "center", padding: 20 }}>No AXEL usage yet today.</div>
        ) : (
          <div data-testid="axel-top-users" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {stats.top_users_today.map((u, i) => (
              <div key={u.user_id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "8px 12px", background: B.bg, borderRadius: 8,
                border: `1px solid ${B.border}`,
              }}>
                <span style={{ color: B.gold, fontSize: 12, fontWeight: 800, width: 22 }}>#{i + 1}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: B.white, fontSize: 13, fontWeight: 600 }}>{u.name}</div>
                  <div style={{ color: B.gray, fontSize: 11 }}>{u.email}</div>
                </div>
                <span style={{
                  padding: "2px 8px", borderRadius: 999,
                  background: u.role === "admin" ? `${B.gold}25` : `${B.border}`,
                  color: u.role === "admin" ? B.gold : B.whiteGray,
                  fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                }}>{u.role}</span>
                <span style={{ color: B.white, fontSize: 14, fontWeight: 700, minWidth: 40, textAlign: "right" }}>{u.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
